import type { FunctionComponent, MouseEvent } from 'react'
import React, { useState } from 'react'

import { CarouselProvider } from 'pure-react-carousel'

import { TypographyV2 } from '../../Seatbelt2/Foundations/Typography'
import { Buttons } from './components/Buttons'
import { Carousel } from './components/Carousel'
import { Thumbnails } from './components/Thumbnails'
import styles from './ImageGallery.module.scss'
import type { Images } from './imageType'

export const ImageGallery: FunctionComponent<Props> = ({
  title,
  images,
  url,
  className,
  carouselBgColor = 'grey',
  showCount = true,
  portalZIndex,
  traverseGalleryHandler,
  thumbnailClickHandler,
}) => {
  const [current, setCurrent] = useState(0)

  if (!imagesAreValid(images)) {
    return null
  }

  return (
    <section data-testid="image-gallery" className={className}>
      <div className={styles.screen}>
        <TypographyV2
          className={styles.title}
          textStyle="sb-text-heading-small"
        >
          {title}
        </TypographyV2>

        <CarouselProvider
          totalSlides={images.length}
          orientation="horizontal"
          currentSlide={current}
          aria-label={`Carousel of ${images.length} images`}
          naturalSlideHeight={0}
          naturalSlideWidth={0}
        >
          <figure className={styles.figure}>
            <div className={styles.carousel}>
              <Carousel
                current={current}
                images={images}
                onChange={setCurrent}
                portalZIndex={portalZIndex}
                carouselBgColor={carouselBgColor}
              />
              <Buttons
                current={current}
                total={images.length}
                onChange={setCurrent}
                traverseGalleryHandler={traverseGalleryHandler}
              />
            </div>

            <div
              className={styles.caption}
              aria-live="polite"
              aria-atomic="true"
            >
              <TypographyV2
                tag="figcaption"
                textStyle="sb-text-interface-body-small-regular"
              >
                {images[current].caption ?? ''}
              </TypographyV2>

              {showCount && (
                <TypographyV2
                  data-testid="count"
                  className={styles.count}
                  textStyle="sb-text-interface-body-small-regular"
                >
                  {current + 1} / {images.length}
                </TypographyV2>
              )}
            </div>
          </figure>

          <Thumbnails
            className={styles.thumbnails}
            images={images}
            current={current}
            onChange={setCurrent}
            thumbnailClickHandler={thumbnailClickHandler}
          />
        </CarouselProvider>
      </div>

      {url && (
        <TypographyV2
          textStyle="sb-text-interface-body-small-regular"
          className={styles.print}
        >
          A large collection of images displayed on this page are available at{' '}
          <a href={url} className={styles.anchor}>
            {url}
          </a>
        </TypographyV2>
      )}
    </section>
  )
}
///////// IMPLEMENTATION /////////

type Props = {
  /**
   * Image gallery title
   */
  title?: string
  /**
   * Array of image objects
   */
  images: Array<Images>
  /**
   * Link url
   */
  url?: string
  /**
   * Carousel background colour to be added to the main image
   */
  carouselBgColor?: 'grey' | 'white'
  /**
   * Class Name to be added to the component to add additional styling/positioning
   */
  className?: string
  /**
   * Prop that toggles the show of the count element within the gallery
   */
  showCount?: boolean
  /**
   * Prop that applies a z-index to the carousel portal
   */
  portalZIndex?: number
  /**
   * Event handler that is invoked when traversing gallery images using the next/previous buttons.
   */
  traverseGalleryHandler?: (e: MouseEvent<HTMLButtonElement>) => void
  /**
   * Event handler that is invoked when selecting image thumbnails.
   */
  thumbnailClickHandler?: (index: number) => void
}

const imagesAreValid = (images: Props['images']) => {
  if (Array.isArray(images) && images.length) {
    return true
  }

  return false
}
